import { useCallback, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import LineRefreshIcon from 'LuxKit/Icons/line/LineRefreshIcon'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import MarkdownRender from 'LuxKit/MarkdownRender'
import { Heading4Style, Heading5Style } from 'LuxKit/Typography/Heading'
import { useSalesAgentSummary } from 'tripPlanner/hooks/api/chat'

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  padding: ${rem(12)} ${rem(20)};
  box-shadow: ${props => props.theme.shadow.bottom.small};
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
  margin-bottom: ${rem(8)};
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`

const SummaryMarkdownWrapper = styled.div`
  > div {
    font-size: ${rem(16)};
    line-height: 1.45;
  }

  h2 {
    ${Heading4Style}
    margin-top: ${rem(12)};
    margin-bottom: ${rem(8)};
  }

  h3 {
    ${Heading5Style}
    margin-top: ${rem(12)};
    margin-bottom: ${rem(8)};
  }

  ul,
  ol {
    margin-top: ${rem(8)};
  }

  li {
    margin-top: ${rem(2)};
  }
`

const ActionRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${rem(8)};
`

interface Props {
  chatId: number | undefined
  enabled: boolean
  initiallyCollapsed?: boolean
}

function SalesAgentSummaryPanel({ chatId, enabled, initiallyCollapsed = false }: Props) {
  const intl = useIntl()
  const [isCollapsed, setIsCollapsed] = useState(initiallyCollapsed)
  const {
    data,
    isFetching,
    isError,
    refetch,
  } = useSalesAgentSummary(chatId, { enabled: enabled && !!chatId })

  const handleRegenerateSummary = useCallback(() => {
    refetch()
  }, [refetch])

  const handleCollapseSummary = useCallback(() => {
    setIsCollapsed(true)
  }, [])

  const handleExpandSummary = useCallback(() => {
    setIsCollapsed(false)
  }, [])

  if (!enabled || !chatId) {
    return null
  }

  const heading = intl.formatMessage({
    id: 'BLG3CMkQiaL_0b7Mm6Qo',
    defaultMessage: 'Sales summary',
    description: 'Heading for the staff-only sales summary shown in Trip Planner spoof mode',
  })
  const regenerateLabel = intl.formatMessage({
    id: 'bSCbfyzE6_PpsFN8pQRu',
    defaultMessage: 'Regenerate sales summary',
    description: 'Accessible label for the button that regenerates the staff-only Trip Planner sales summary',
  })
  const loadingLabel = intl.formatMessage({
    id: '0QNB8yk1tLHmf5gRnoHD',
    defaultMessage: 'Generating sales summary',
    description: 'Loading text while the staff-only Trip Planner sales summary is generated',
  })
  const errorLabel = intl.formatMessage({
    id: '3ouQhk3eP8Ltx9y8V0fC',
    defaultMessage: 'Summary unavailable. Try regenerating it.',
    description: 'Error text when the staff-only Trip Planner sales summary fails to load',
  })
  const emptyLabel = intl.formatMessage({
    id: 'xwLJf88dZEmHnRWZjEmK',
    defaultMessage: 'No summary available yet.',
    description: 'Empty text when the staff-only Trip Planner sales summary response is blank',
  })
  const collapseLabel = intl.formatMessage({
    id: 'xhNdpd1d3r6gvK2M_ODn',
    defaultMessage: 'Close summary',
    description: 'Button text that collapses the staff-only Trip Planner sales summary panel',
  })
  const expandLabel = intl.formatMessage({
    id: 'ZhMBi7rvKn0c8nT797UB',
    defaultMessage: 'Open summary',
    description: 'Button text that expands the staff-only Trip Planner sales summary panel',
  })

  return (
    <Panel aria-label={heading}>
      <PanelHeader>
        <Caption variant="medium" weight="bold" colour="neutral-one">
          {heading}
        </Caption>
        <HeaderActions>
          {isCollapsed && (
            <TextButton
              kind="primary"
              size="small"
              onClick={handleExpandSummary}
            >
              {expandLabel}
            </TextButton>
          )}
          <IconButton
            kind="tertiary"
            size="small"
            aria-label={regenerateLabel}
            disabled={isFetching}
            onClick={handleRegenerateSummary}
          >
            <LineRefreshIcon />
          </IconButton>
        </HeaderActions>
      </PanelHeader>
      {!isCollapsed && isFetching && !data && (
        <SummaryMarkdownWrapper aria-label={loadingLabel}>
          <LoadingBox height={16} borderRadius="S" style={{ width: '100%' }} />
          <LoadingBox height={16} borderRadius="S" style={{ width: '70%' }} />
        </SummaryMarkdownWrapper>
      )}
      {!isCollapsed && isError && !isFetching && (
        <BodyText variant="small" colour="critical">
          {errorLabel}
        </BodyText>
      )}
      {!isCollapsed && !isError && data && (
        <SummaryMarkdownWrapper>
          <MarkdownRender
            content={data.summary || emptyLabel}
            fontSize="small"
            type="compact"
          />
        </SummaryMarkdownWrapper>
      )}
      {!isCollapsed && <ActionRow>
        <TextButton
          kind="primary"
          size="small"
          onClick={handleCollapseSummary}
        >
          {collapseLabel}
        </TextButton>
      </ActionRow>}
    </Panel>
  )
}

export default SalesAgentSummaryPanel
